import { forwardRef, useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Box, useTheme } from '@mui/material'

export type PinCellStyle = {
	height: number
	gap: number
	radius: number
	fontSize: string
	fontFamily?: string
	background: string
	border: string
	borderWidth: number
	accent: string
	focusWidth: number
	color: string
}

type Props = {
	value: string
	onChange: (value: string) => void
	onComplete?: (value: string) => void
	onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
	length?: number
	autoFocus?: boolean
	disabled?: boolean
	/** Show dots instead of digits. */
	masked?: boolean
	/**
	 * Verification codes (email/SMS): let the browser offer the received code
	 * (autocomplete="one-time-code"). PINs leave this off so nothing is ever offered.
	 */
	codeAutofill?: boolean
	cellStyle?: Partial<PinCellStyle>
}

/**
 * PIN / verification-code entry drawn as separate cells but backed by ONE invisible text input.
 * Browsers and password managers offer autofill (saved usernames/passwords, codes from
 * SMS/email) to password fields and to rows of single-character fields; a lone,
 * unlabelled numeric text field with autocomplete="off" gets neither. With codeAutofill
 * the same single input opts in to one-time-code suggestions instead.
 */
const PASSWORD_MANAGER_OPT_OUT = {
	'data-lpignore': 'true',
	'data-1p-ignore': 'true',
	'data-bwignore': 'true',
	'data-form-type': 'other',
} as const

const PinCodeField = forwardRef<HTMLDivElement, Props>(function PinCodeField(
	{ value, onChange, onComplete, onKeyDown, length = 4, autoFocus, disabled, masked = true, codeAutofill = false, cellStyle },
	ref
) {
	const theme = useTheme()
	const s: PinCellStyle = {
		height: 40,
		gap: 8,
		radius: 4,
		fontSize: '1.15rem',
		background: 'transparent',
		border: theme.palette.divider,
		borderWidth: 1,
		accent: theme.palette.primary.main,
		focusWidth: 2,
		color: theme.palette.text.primary,
		...cellStyle,
	}
	const inputRef = useRef<HTMLInputElement>(null)
	const [focused, setFocused] = useState(false)

	// Like the autoFocus attribute, once — but wait until enabled: screens often mount the
	// field while the request that opened them is still finishing (disabled inputs can't focus).
	const autoFocusedRef = useRef(false)
	useEffect(() => {
		if (!autoFocus || disabled || autoFocusedRef.current) return
		autoFocusedRef.current = true
		inputRef.current?.focus()
	}, [autoFocus, disabled])

	// Typing always appends; keep the caret at the end so arrows/clicks can't insert mid-PIN.
	const keepCaretAtEnd = () => {
		const el = inputRef.current
		if (el && el.selectionStart !== el.value.length) el.setSelectionRange(el.value.length, el.value.length)
	}

	const activeIndex = Math.min(value.length, length - 1)

	return (
		<Box
			ref={ref}
			onMouseDown={(e) => {
				if (disabled) return
				e.preventDefault()
				inputRef.current?.focus()
			}}
			sx={{
				position: 'relative',
				display: 'flex',
				gap: `${s.gap}px`,
				width: '100%',
				maxWidth: '408px',
				alignSelf: 'center',
				opacity: disabled ? 0.6 : 1,
				cursor: disabled ? 'default' : 'text',
			}}
		>
			{Array.from({ length }, (_, i) => {
				const isActive = focused && !disabled && i === activeIndex
				const char = value[i]
				return (
					<Box
						key={i}
						aria-hidden
						sx={{
							flex: '1 1 0',
							minWidth: 0,
							height: s.height,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							borderRadius: `${s.radius}px`,
							backgroundColor: s.background,
							border: `${s.borderWidth}px solid ${isActive ? s.accent : s.border}`,
							// Thicker focus ring without shifting layout.
							boxShadow: isActive ? `inset 0 0 0 ${Math.max(0, s.focusWidth - s.borderWidth)}px ${s.accent}` : 'none',
							color: s.color,
							fontSize: s.fontSize,
							fontFamily: s.fontFamily,
							userSelect: 'none',
							transition: 'border-color 120ms ease',
						}}
					>
						{char ? (masked ? '•' : char) : ''}
					</Box>
				)
			})}
			<input
				ref={inputRef}
				value={value}
				disabled={disabled}
				onChange={(e) => {
					const next = e.target.value.replace(/\D/g, '').slice(0, length)
					onChange(next)
					if (onComplete && next.length === length && next !== value) onComplete(next)
				}}
				onKeyDown={onKeyDown}
				onFocus={() => {
					setFocused(true)
					keepCaretAtEnd()
				}}
				onBlur={() => setFocused(false)}
				onSelect={keepCaretAtEnd}
				type="text"
				inputMode="numeric"
				pattern="[0-9]*"
				maxLength={length}
				autoComplete={codeAutofill ? 'one-time-code' : 'off'}
				autoCorrect="off"
				autoCapitalize="off"
				spellCheck={false}
				aria-label={codeAutofill ? `Enter the ${length}-digit verification code` : `Enter ${length} digits`}
				{...(codeAutofill ? {} : PASSWORD_MANAGER_OPT_OUT)}
				style={{
					position: 'absolute',
					inset: 0,
					width: '100%',
					height: '100%',
					margin: 0,
					padding: 0,
					border: 0,
					opacity: 0,
					background: 'transparent',
					color: 'transparent',
					caretColor: 'transparent',
					// 16px stops iOS zooming into the field on focus.
					fontSize: 16,
					cursor: disabled ? 'default' : 'text',
				}}
			/>
		</Box>
	)
})

export default PinCodeField
